import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Sparkles, Check, ShieldCheck, Clock, Settings2, X } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const fmtBRL = (n) => Number(n || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const pollCheckout = async (sessionId, attempts = 0) => {
  if (attempts >= 8) return { status: 'timeout' };
  try {
    const { data } = await axios.get(`${BACKEND_URL}/api/billing/checkout/${sessionId}`, { withCredentials: true });
    if (data.payment_status === 'paid') return data;
    if (data.status === 'expired') return { status: 'expired' };
    await new Promise((r) => setTimeout(r, 2000));
    return pollCheckout(sessionId, attempts + 1);
  } catch (e) {
    if (attempts === 0) throw e;
    await new Promise((r) => setTimeout(r, 2000));
    return pollCheckout(sessionId, attempts + 1);
  }
};

export const BillingPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, billing, refreshBilling } = useAuth();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(null);
  const [returnState, setReturnState] = useState(null); // 'processing' | 'success' | 'canceled'

  const loadPlans = useCallback(async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/plans/public`);
      setPlans(data || []);
    } catch (e) {
      toast.error('Não foi possível carregar os planos.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPlans();
    refreshBilling?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle return from Stripe Checkout
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const sessionId = params.get('session_id');
    const canceled = params.get('canceled');
    if (canceled) {
      setReturnState('canceled');
      toast.info('Pagamento cancelado. Você pode tentar novamente quando quiser.');
      navigate('/billing', { replace: true });
      return;
    }
    if (sessionId) {
      setReturnState('processing');
      pollCheckout(sessionId)
        .then((res) => {
          if (res.payment_status === 'paid') {
            setReturnState('success');
            toast.success('Assinatura confirmada! Bem-vindo ao MeAdd.');
            refreshBilling?.();
          } else {
            setReturnState('canceled');
            toast.error('Pagamento ainda em processamento ou expirado.');
          }
          navigate('/billing', { replace: true });
        })
        .catch(() => {
          setReturnState('canceled');
          toast.error('Não foi possível confirmar o pagamento. Tente novamente.');
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startCheckout = async (plan) => {
    setSubmitting(plan.id);
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/billing/checkout`,
        { plan_id: plan.id, origin_url: window.location.origin },
        { withCredentials: true }
      );
      window.location.href = data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Falha ao iniciar checkout.');
      setSubmitting(null);
    }
  };

  const openPortal = async () => {
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/billing/portal`,
        { origin_url: window.location.origin },
        { withCredentials: true }
      );
      window.location.href = data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Não foi possível abrir o portal.');
    }
  };

  const isAdmin = user?.role === 'admin';
  const subStatus = billing?.subscription_status;
  const hasActiveSub = subStatus === 'active' || subStatus === 'past_due';

  return (
    <Layout>
      <div className="max-w-5xl mx-auto space-y-8" data-testid="billing-page">
        <div className="flex flex-col gap-3">
          <h1 className="text-3xl font-bold text-slate-900 font-outfit">Planos e cobrança</h1>
          <p className="text-slate-600 font-manrope">
            Continue sua jornada na MeAdd escolhendo um plano que combina com o tamanho da sua operação.
          </p>
        </div>

        {returnState === 'processing' && (
          <Card className="border-amber-200 bg-amber-50" data-testid="billing-processing">
            <CardContent className="p-5 flex items-center gap-3">
              <div className="animate-spin h-5 w-5 border-2 border-amber-600 border-t-transparent rounded-full" />
              <div className="text-amber-900 font-manrope text-sm">
                Confirmando seu pagamento com o Stripe... Isso costuma levar até 10 segundos.
              </div>
            </CardContent>
          </Card>
        )}

        {billing && (
          <Card className="border-slate-200">
            <CardContent className="p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4 justify-between">
              <div className="flex items-center gap-3">
                <div className="h-11 w-11 rounded-xl bg-slate-100 flex items-center justify-center">
                  {billing.blocked ? <ShieldCheck className="h-5 w-5 text-rose-600" /> : <Clock className="h-5 w-5 text-slate-700" />}
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold font-manrope">Status atual</div>
                  <div className="text-lg font-semibold text-slate-900" data-testid="billing-current-status">
                    {hasActiveSub
                      ? `Assinatura ativa — ${billing.plan?.name || 'Plano pago'}`
                      : billing.blocked
                        ? 'Trial expirado (modo somente leitura)'
                        : billing.is_trial && typeof billing.days_left === 'number'
                          ? `Trial — ${billing.days_left} ${billing.days_left === 1 ? 'dia restante' : 'dias restantes'}`
                          : billing.plan?.name || 'Ativo'}
                  </div>
                </div>
              </div>
              {hasActiveSub && isAdmin && (
                <Button onClick={openPortal} variant="outline" data-testid="billing-portal-btn" className="gap-2">
                  <Settings2 className="h-4 w-4" /> Gerenciar assinatura
                </Button>
              )}
              {!hasActiveSub && billing.trial_ends_at && (
                <div className="text-xs text-slate-500 font-manrope">
                  Trial até {new Date(billing.trial_ends_at).toLocaleDateString('pt-BR')}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        <div className="grid md:grid-cols-2 gap-6">
          {loading && <div className="col-span-2 text-center text-slate-500 py-10">Carregando planos...</div>}
          {plans.map((p) => {
            const isCurrent = hasActiveSub && billing?.plan?.id === p.id;
            return (
              <Card
                key={p.id}
                data-testid={`billing-plan-${p.id}`}
                className={`relative border-2 ${p.destaque ? 'border-emerald-500 shadow-lg' : 'border-slate-200'}`}
              >
                {p.destaque && (
                  <div className="absolute -top-3 left-6 bg-emerald-500 text-white text-xs font-semibold px-3 py-1 rounded-full uppercase tracking-wider font-manrope">
                    <Sparkles className="h-3 w-3 inline-block mr-1" /> Mais escolhido
                  </div>
                )}
                {isCurrent && (
                  <div className="absolute -top-3 right-6 bg-slate-900 text-white text-xs font-semibold px-3 py-1 rounded-full uppercase tracking-wider font-manrope">
                    Plano atual
                  </div>
                )}
                <CardContent className="p-7 space-y-5">
                  <div>
                    <div className="text-lg font-semibold text-slate-900 font-outfit">{p.name}</div>
                    <div className="mt-2 flex items-baseline gap-1">
                      <span className="text-4xl font-bold text-slate-900">{fmtBRL(p.price_brl)}</span>
                      <span className="text-sm text-slate-500 font-manrope">/mês</span>
                    </div>
                  </div>
                  <ul className="space-y-2 text-sm text-slate-700 font-manrope">
                    <li className="flex items-center gap-2"><Check className="h-4 w-4 text-emerald-500" /> {p.max_apoiadores ? `${p.max_apoiadores.toLocaleString('pt-BR')} apoiadores` : 'Apoiadores ilimitados'}</li>
                    <li className="flex items-center gap-2"><Check className="h-4 w-4 text-emerald-500" /> {p.max_liderancas ? `${p.max_liderancas.toLocaleString('pt-BR')} lideranças` : 'Lideranças ilimitadas'}</li>
                    {p.feature_mobilizacao && <li className="flex items-center gap-2"><Check className="h-4 w-4 text-emerald-500" /> Mobilização completa</li>}
                    {p.feature_financeiro && <li className="flex items-center gap-2"><Check className="h-4 w-4 text-emerald-500" /> Módulo Financeiro</li>}
                    {!p.feature_financeiro && <li className="flex items-center gap-2 text-slate-400"><X className="h-4 w-4" /> Módulo Financeiro</li>}
                    {p.feature_heatmap && <li className="flex items-center gap-2"><Check className="h-4 w-4 text-emerald-500" /> Heatmap geográfico</li>}
                    {p.feature_network && <li className="flex items-center gap-2"><Check className="h-4 w-4 text-emerald-500" /> Network graph 2D</li>}
                    {!p.feature_network && <li className="flex items-center gap-2 text-slate-400"><X className="h-4 w-4" /> Network graph</li>}
                  </ul>
                  <Button
                    className={`w-full h-11 font-semibold ${
                      isCurrent
                        ? 'bg-slate-200 hover:bg-slate-200 text-slate-700 cursor-default'
                        : p.destaque
                          ? 'bg-gradient-to-r from-amber-400 via-amber-500 to-orange-500 hover:from-amber-500 hover:to-orange-600 text-slate-900 shadow-lg shadow-amber-500/30'
                          : 'bg-slate-900 hover:bg-slate-800 text-white'
                    }`}
                    onClick={() => !isCurrent && startCheckout(p)}
                    disabled={submitting === p.id || isCurrent || !isAdmin}
                    data-testid={`billing-plan-${p.id}-cta`}
                  >
                    {isCurrent
                      ? 'Plano ativo'
                      : submitting === p.id
                        ? 'Abrindo Stripe...'
                        : `Assinar ${p.name}`}
                  </Button>
                  {!isAdmin && !isCurrent && (
                    <p className="text-[11px] text-slate-500 text-center font-manrope">
                      Apenas o owner do tenant pode assinar.
                    </p>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>

        <p className="text-xs text-slate-500 text-center font-manrope">
          Pagamento processado com segurança pelo Stripe. Cancele a qualquer momento pelo portal.
        </p>
      </div>
    </Layout>
  );
};

export default BillingPage;
